import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { formatLongDate, formatShortDate, relativeDay, type ISODate } from '@app/shared'

import { useClock } from '../../lib/data'
import { useCapture } from '../shell/CaptureContext'
import { MChevronLeft } from '../shell/icons'
import { Band, BandHeading, BandTile, Dot, SCROLL_BOTTOM } from '../shell/parts'
import { Composer } from '../today/Composer'

// ============================================================================
// ONE PREVIOUS DAY, EDITABLE — JRN-1c.
//
// THE SAME COMPOSER, not a second one. `mobile/today/Composer.tsx` carries the
// undo contract, the durable draft, the restore, the honest save chip and the
// offline retry; a second editor here would be a second set of all of those, and
// the first bug would be one of them drifting. So the card is parametrized (an
// eyebrow label, and a recorder that is only handed over on today) and this
// screen is a band, a back control and that card.
//
// WHAT IS DELIBERATELY NOT HERE: the record row. A voice memo is a TODAY thing —
// recorded now, queued now, transcribed by a worker running now — and a
// microphone on last Tuesday's page would file this morning's audio under last
// Tuesday. Today's memos stay on Today, and the archive row still counts them.
//
// A DAY THAT WAS NEVER WRITTEN IS NOT AN ERROR. `GET /api/journal/{date}`
// answers `null`, the composer opens empty, and writing in it creates the entry
// — which is exactly what "I forgot to write on Tuesday" needs.
//
// WHERE BACK GOES. The month being read travels in the URL (`?from=`, written by
// the archive), so returning lands on the month the owner was in rather than
// snapping to this one — and a deep link with no `from` still has a back control
// that works, because it is an address rather than a step in a history stack
// that may not exist.
// ============================================================================

export function MobileJournalDay() {
  const { date } = useParams<{ date: string }>()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { data: clock } = useClock()
  const capture = useCapture()

  const from = params.get('from')
  const tag = params.get('tag')
  // Back lands on the list as it was being read — same month, same tag filter if there was one.
  const backTo =
    from && /^\d{4}-\d{2}-\d{2}$/.test(from)
      ? `/journal?month=${from}${tag ? `&tag=${encodeURIComponent(tag)}` : ''}`
      : '/journal'
  const back = (
    <BandTile label="Back to the journal" onClick={() => navigate(backTo)}>
      <MChevronLeft />
    </BandTile>
  )

  // A malformed date in the URL is not a day. Saying so beats rendering a composer pointed at a
  // key the API will refuse, which would read as a failing save rather than a bad link.
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return (
      <>
        <Band band="today">
          <BandHeading title="Not a day" subline={<span>Nothing to open</span>} action={back} />
        </Band>
        <div className="px-[18px] pt-3">
          <p className="text-[12.5px] leading-[1.5] text-microcopy text-pretty">
            <span className="font-medium">{date ?? 'That address'}</span> is not a date, so there is
            no entry behind it. The journal is listed by month on the previous screen.
          </p>
        </div>
      </>
    )
  }

  const day = date as ISODate
  const isToday = clock?.date === day

  return (
    <>
      <Band band="today">
        <BandHeading
          title={formatLongDate(day)}
          action={back}
          subline={
            <>
              <span className="font-medium">Journal history</span>
              {clock && (
                <>
                  <Dot />
                  <span>{relativeDay(day, clock.date)}</span>
                </>
              )}
            </>
          }
        />
      </Band>

      <div
        className="flex min-h-0 flex-1 flex-col gap-[12px] overflow-y-auto overflow-x-hidden px-[14px] pt-[2px]"
        style={{ paddingBottom: SCROLL_BOTTOM }}
      >
        {/* TODAY REACHED THROUGH THIS ROUTE IS STILL TODAY: the recorder is handed over, so
            /journal/<today> is the same card the Today tab shows and not a crippled copy. */}
        <Composer
          date={day}
          nowMinutes={clock?.minutesIntoDay ?? 0}
          label={isToday ? 'Today’s entry' : `${formatShortDate(day)} entry`}
          onRecord={isToday ? capture.startRecording : undefined}
          onRecordRelease={isToday ? capture.stopRecording : undefined}
        />
      </div>
    </>
  )
}
